/**
 * Predicate + parser for v0.4 Stage B Blob Handler error reasons.
 *
 * Lifted out of `BlobHandlerErrorPanel.tsx` so the component file
 * exports only the component (Fast Refresh requirement) and the
 * predicate stays unit-testable in isolation.
 *
 * Two error-reason shapes are recognised:
 *
 *   - ``"File type [<mime>] cannot be processed as tabular data."`` —
 *     emitted by FIA's BlobHandler integration for images / audio /
 *     unknown types.
 *
 *   - ``"PDF could not be converted to tabular data (<reason>)."`` —
 *     emitted for text-only PDFs and PDF safety-cap rejections
 *     (file_too_large, too_many_pages, extracted_too_many_rows,
 *     no_tables_detected).
 */

export interface ParsedBlobError {
  /** Short headline for the panel — one sentence. */
  headline: string;
  /** Detail line under the headline — names the detected type or reason. */
  detail: string;
  /** Three-or-fewer "what to do next" bullets. */
  suggestions: string[];
}

/**
 * Detect whether a run's ``error_reason`` looks like a Blob Handler
 * rejection. Used as a guard before rendering the panel.
 */
export function isBlobHandlerError(
  errorReason: string | undefined | null,
): boolean {
  if (!errorReason) return false;
  return (
    errorReason.startsWith('File type [')
    || errorReason.includes('could not be converted to tabular data')
  );
}

export function parseBlobError(errorReason: string): ParsedBlobError {
  // ---- Image / audio / unknown ("File type [<mime>] cannot…") ----
  const fileTypeMatch = errorReason.match(/File type \[([^\]]+)\]/);
  if (fileTypeMatch) {
    const mime = fileTypeMatch[1];
    if (mime.startsWith('image/')) {
      return {
        headline: 'This file is an image, not tabular data.',
        detail: `Detected file type: ${mime}.`,
        suggestions: [
          "If the image contains a table, transcribe it into a spreadsheet (e.g. Excel) and export as CSV.",
          "Take a screenshot of the rendered table only if the original spreadsheet is unavailable.",
          "Re-upload the new CSV or Excel file.",
        ],
      };
    }
    if (mime.startsWith('audio/')) {
      return {
        headline: 'This file is audio, not tabular data.',
        detail: `Detected file type: ${mime}.`,
        suggestions: [
          "Audio files don't contain tabular data Raydn can process.",
          "If a transcription exists, structure it into a spreadsheet and export as CSV.",
        ],
      };
    }
    return {
      headline: 'This file type cannot be processed as tabular data.',
      detail: `Detected file type: ${mime}.`,
      suggestions: [
        "Extract your data into a spreadsheet (Excel / CSV) and re-upload.",
        "Confirm the file isn't corrupted — open it locally to verify the contents.",
      ],
    };
  }

  // ---- PDF: text-only / safety-cap rejection ----
  if (errorReason.includes('could not be converted to tabular data')) {
    const reasonMatch = errorReason.match(/\(([^)]+)\)/);
    const reason = reasonMatch ? reasonMatch[1] : 'unspecified';

    if (reason.startsWith('no_tables_detected')) {
      return {
        headline: 'No tables were detected in this PDF.',
        detail:
          "Raydn extracts tabular data from PDFs using pdfplumber, but no extractable tables were found in this document.",
        suggestions: [
          "If the PDF is scanned or image-based, OCR it first and convert to CSV.",
          "If the PDF contains data laid out as text rather than a table, transcribe it into a spreadsheet.",
          "Re-upload the resulting CSV or Excel file.",
        ],
      };
    }
    if (reason.startsWith('file_too_large')) {
      return {
        headline: 'PDF too large to process.',
        detail: `Reason: ${reason}.`,
        suggestions: [
          "Raydn caps PDF inputs at 50 MB to bound processing time.",
          "Split the PDF into smaller files, or extract the relevant tables and upload them as CSV.",
        ],
      };
    }
    if (reason.startsWith('too_many_pages')) {
      return {
        headline: 'PDF has too many pages.',
        detail: `Reason: ${reason}.`,
        suggestions: [
          "Raydn caps PDF inputs at 500 pages.",
          "Split the PDF into smaller files, or extract the relevant tables and upload them as CSV.",
        ],
      };
    }
    if (reason.startsWith('extracted_too_many_rows')) {
      return {
        headline: 'PDF table is too large to extract.',
        detail: `Reason: ${reason}.`,
        suggestions: [
          "Raydn caps extracted tables at 100,000 rows to bound memory use.",
          "If the PDF data is in a smaller file (e.g. the original spreadsheet), upload that instead.",
        ],
      };
    }
    return {
      headline: 'PDF could not be converted to tabular data.',
      detail: `Reason: ${reason}.`,
      suggestions: [
        "Re-export the source data from its original system as CSV or Excel.",
        "If the PDF is the only source, manually transcribe the data into a spreadsheet.",
      ],
    };
  }

  // Unknown shape — generic fallback.
  return {
    headline: 'File could not be processed as tabular data.',
    detail: errorReason,
    suggestions: [
      "Extract the data into a spreadsheet (CSV or Excel) and re-upload.",
    ],
  };
}
